import { Controller } from "@hotwired/stimulus"
import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"

// Things a click is meant to OPERATE rather than comment on. `.course-review-add` and
// `.course-review-marker` are the review layer's own controls and are handled
// separately, so they are deliberately absent here.
const CONTROL_SELECTOR = 'button, input, select, textarea, label, summary, a[href], [draggable="true"]'

// Review mode's in-canvas layer: it decorates every commentable element, opens the
// floating composer on the ⊕ chip, and opens the thread popover on an element that
// already has comments.
//
// The composer and the thread are appended to <body>, not to the canvas, because
// the canvas scrolls and clips — a floating card anchored near its edge would be cut
// off. Floating UI (the same mechanism the slash menu uses) keeps them on-screen.
export default class extends Controller {
  static targets = ["composerTemplate", "addButtonTemplate", "threadTemplate", "threadItemTemplate", "threadAttachmentTemplate"]
  static values = {
    createUrl: String,
    attachmentUrl: String,
    courseId: String,
    anchors: Object,
    mentions: Array,
    // Reviewers may open new threads (the ⊕ chip); the author reads and replies but
    // never opens top-level threads on their own course, so the chip is withheld.
    canComment: Boolean
  }

  connect() {
    this.decorateAnchors()
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    this.closeComposer()
    this.closeThread()
  }

  // ---- Anchor decoration ----------------------------------------------------

  // Child rows are stamped by the content-item partials with the bare child key;
  // the block id lives on the ancestor. Blocks stamp their own id. Both become a
  // `.course-review-anchor` here so one CSS contract covers them.
  decorateAnchors() {
    this.element.querySelectorAll("[data-review-anchor]").forEach((anchor) => {
      const key = this.anchorKeyFor(anchor)
      if (!key) return

      anchor.classList.add("course-review-anchor")
      anchor.dataset.reviewAnchorKey = key

      // Child rows / section fields get their ⊕ chip from here (blocks stamp theirs
      // server-side). The author has no chip anywhere, so it is skipped for them.
      if (this.canCommentValue && !this.isBlockAnchor(anchor)) this.addChipTo(anchor)
      this.paintCommentedState(anchor, key)
    })
  }

  isBlockAnchor(anchor) {
    return anchor.hasAttribute("data-course-builder-block-id")
  }

  // "<lesson_content_id>" for a block, "<lesson_content_id>:<child_key>" for a row,
  // and "<section>:<child_key>" for an element on a non-curriculum screen — the same
  // formats CourseReviewComment#anchor_key builds server-side.
  anchorKeyFor(anchor) {
    const raw = anchor.getAttribute("data-review-anchor")
    if (!raw) return null
    if (this.isBlockAnchor(anchor)) return raw

    // A section screen wraps its anchors in [data-review-section]. Checked before the
    // curriculum block id so an assessment question (which also sits inside a numeric
    // data-course-builder-block-id wrapper) anchors by its section, not by an id that
    // would masquerade as a lesson_content.
    const section = anchor.closest("[data-review-section]")?.getAttribute("data-review-section")
    if (section) return `${section}:${raw}`

    const blockId = anchor.closest("[data-course-builder-block-id]")?.getAttribute("data-course-builder-block-id")
    if (!blockId) return null

    return `${blockId}:${raw}`
  }

  addChipTo(anchor) {
    if (!this.hasAddButtonTemplateTarget) return
    if (anchor.querySelector(":scope > .course-review-add")) return

    const chip = this.addButtonTemplateTarget.content.firstElementChild.cloneNode(true)
    chip.setAttribute("aria-label", `Add a review comment on ${this.labelFor(anchor)}`)
    if (this.isInteractiveAnchor(anchor)) this.insulateChip(chip, anchor)
    anchor.appendChild(chip)
  }

  // An anchor the reviewer is meant to OPERATE rather than only read — a quiz option,
  // a sorting row, a flip card. Declared in the markup by `academy_review_anchor`
  // rather than sniffed from class names, so a new activity opts in explicitly.
  isInteractiveAnchor(anchor) {
    return anchor.hasAttribute("data-review-interactive")
  }

  // The chip is a child of the anchor, so the host's own handlers see everything the
  // chip does. Two of them would misread it:
  //   - a sorting row is draggable="true", so a drag begun on the chip reorders the row;
  //   - the same row listens for keydown to move itself, and keydown bubbles, so
  //     arrowing while the chip has focus silently reorders the list.
  // Neither is reachable from the chip's own markup, so it is insulated here.
  insulateChip(chip, anchor) {
    chip.setAttribute("draggable", "false")
    if (anchor.getAttribute("draggable") === "true" || anchor.hasAttribute("tabindex")) {
      chip.addEventListener("keydown", (event) => event.stopPropagation())
    }
  }

  paintCommentedState(anchor, key) {
    const entry = this.anchorsValue[key]
    // Only PENDING feedback lights an element — an anchor whose comments are all
    // resolved/dismissed is not highlighted (and, having no avatars, no marker).
    if (!entry || !entry.pending_count) return

    anchor.classList.add("is-commented")
    anchor.dataset.reviewCommentCount = entry.count

    const marker = this.markerTemplateFor(key)
    if (marker && !anchor.querySelector(":scope > .course-review-marker")) anchor.appendChild(marker)
  }

  markerTemplateFor(key) {
    const template = this.element.querySelector(`.course-review-marker-template[data-anchor-key="${CSS.escape(key)}"]`)

    return template ? template.content.firstElementChild.cloneNode(true) : null
  }

  // The block stamps its own human name server-side (data-review-label). Reading
  // textContent instead would scoop up every control in the subtree — the toolbar's
  // "Background color", a takeaway's placeholder — and produce a wall of noise.
  // Child rows carry no label of their own and inherit the block's.
  labelFor(anchor) {
    return anchor.closest("[data-review-label]")?.dataset.reviewLabel || "this element"
  }

  // ---- Composer -------------------------------------------------------------

  openComposer(event) {
    event.preventDefault()
    event.stopPropagation()
    // Opening a new thread is reviewer-only; the author reaches the composer only as a
    // reply hosted inside an existing thread (mountComposer with a parentId).
    if (!this.canCommentValue) return

    const trigger = event.currentTarget
    const anchor = trigger.closest("[data-review-anchor]")
    if (!anchor) return

    this.closeThread()
    this.mountComposer({ anchor, trigger, reference: trigger })
  }

  mountComposer({ anchor, trigger, reference, parentId = "", host = null }) {
    this.closeComposer()

    const composer = this.composerTemplateTarget.content.firstElementChild.cloneNode(true)
    // A section anchor posts its section + child_key; a curriculum anchor splits its
    // "<lesson_content_id>:<child_key>" key. Exactly one of section / lessonContentId
    // is set, which is how the server routes the anchor.
    const sectionContainer = anchor.closest("[data-review-section]")
    let lessonContentId = ""
    let section = ""
    let childKey = ""
    if (sectionContainer) {
      section = sectionContainer.getAttribute("data-review-section")
      childKey = anchor.getAttribute("data-review-anchor") || ""
    } else {
      const key = anchor.dataset.reviewAnchorKey || this.anchorKeyFor(anchor)
      ;[lessonContentId, childKey = ""] = key.split(":")
    }

    Object.assign(composer.dataset, {
      courseReviewComposerCreateUrlValue: this.createUrlValue,
      courseReviewComposerAttachmentUrlValue: this.attachmentUrlValue,
      courseReviewComposerCourseIdValue: this.courseIdValue,
      courseReviewComposerLessonContentIdValue: lessonContentId,
      courseReviewComposerSectionValue: section,
      courseReviewComposerChildKeyValue: childKey,
      courseReviewComposerParentIdValue: parentId,
      courseReviewComposerPlaceholderValue: parentId ? "Reply…" : `Add a Comment on ${this.labelFor(anchor)}…`,
      courseReviewMentionsCandidatesValue: JSON.stringify(this.mentionsValue)
    })
    if (parentId) composer.classList.add("course-review-composer--reply")

    this.composer = composer
    this.composerAnchor = anchor
    this.composerTrigger = trigger
    anchor.classList.add("has-review-composer")
    ;(host || document.body).appendChild(composer)

    if (host) return
    this.composerCleanup = this.floatOn(reference, composer, "bottom")
  }

  closeComposer({ returnFocus = false } = {}) {
    if (!this.composer) return

    this.composerCleanup?.()
    this.composerCleanup = null
    this.composer.remove()
    this.composer = null
    this.composerAnchor?.classList.remove("has-review-composer")
    this.composerAnchor = null

    const trigger = this.composerTrigger
    this.composerTrigger = null
    if (returnFocus) trigger?.focus()
  }

  // The composer reports its own lifecycle through window events — there are no
  // Stimulus outlets in this project and the composer lives outside this subtree.
  onComposerDismissed(event) {
    this.closeComposer({ returnFocus: event.detail?.returnFocus === true })
  }

  // The yellow highlight is painted optimistically here so the element reacts the
  // instant the comment lands. The avatar cannot be: the server owns that markup,
  // and a first comment on an element has no marker template to clone yet. It
  // arrives with onCommentRefreshed.
  onCommentCreated(event) {
    const anchor = this.element.querySelector(
      `[data-review-anchor-key="${CSS.escape(event.detail.anchorKey)}"]`
    )

    this.closeComposer()
    this.closeThread()
    anchor?.classList.add("is-commented")
  }

  // Fired once the review_activity stream has swapped in fresh markers. Repainting
  // every anchor rather than just the one that changed is what makes deletes and
  // resolves land too — an anchor whose last comment is gone loses its highlight
  // through the same pass.
  onCommentRefreshed() {
    // A resolve or delete from the thread's kebab lands through this same repaint;
    // the open card now shows stale data (or a comment that is gone), so close it.
    this.closeThread()
    this.reloadAnchors()
    this.syncCommentedState()
  }

  reloadAnchors() {
    const markers = this.element.querySelector("#course-review-markers")
    if (!markers?.dataset.courseReviewAnchors) return

    this.anchorsValue = JSON.parse(markers.dataset.courseReviewAnchors)
  }

  syncCommentedState() {
    this.element.querySelectorAll("[data-review-anchor-key]").forEach((anchor) => {
      const key = anchor.dataset.reviewAnchorKey
      const entry = this.anchorsValue[key]

      // Repaint when pending feedback remains; otherwise strip the highlight — this is
      // what makes a resolve/dismiss (or delete) clear the element on the next refresh.
      if (entry && entry.pending_count) {
        this.paintCommentedState(anchor, key)
      } else {
        this.clearCommentedState(anchor)
      }
    })
  }

  clearCommentedState(anchor) {
    anchor.classList.remove("is-commented")
    delete anchor.dataset.reviewCommentCount
    anchor.querySelector(":scope > .course-review-marker")?.remove()
  }

  // ---- Thread ---------------------------------------------------------------

  handleDocumentClick(event) {
    const marker = event.target.closest?.(".course-review-marker")
    const anchor = marker?.closest("[data-review-anchor]") || this.commentedAnchorFrom(event.target)

    if (anchor) {
      event.preventDefault()
      this.openThread(anchor)
      return
    }

    // Close the floating (top-level) composer when the click lands outside it — but
    // never when it lands inside the open thread: the REPLY composer is hosted there,
    // and its own Reply button (which reveals it) would otherwise close it on the same
    // click. A click outside the thread closes both, just below.
    if (this.composer && !this.composer.contains(event.target) &&
        !event.target.closest?.(".course-review-add") &&
        !event.target.closest?.(".course-review-thread")) {
      this.closeComposer()
    }
    if (this.thread && !this.thread.contains(event.target)) this.closeThread()
  }

  commentedAnchorFrom(target) {
    // The author edits inside these elements, so a plain click must not hijack into the
    // comment thread — for them ONLY the marker (comment icon) opens it. A reviewer's
    // canvas is read-only, so click-anywhere-on-a-commented-element stays convenient.
    if (!this.canCommentValue) return null

    const anchor = target.closest?.("[data-review-anchor].is-commented")
    if (!anchor) return null
    if (target.closest?.(".course-review-add")) return null

    // …except where the reviewer is meant to OPERATE the element. Commenting on a quiz
    // option must not cost them the ability to answer it, so on answerable content the
    // marker is the only way in — the same rule the author already gets above, for the
    // same reason. Walking up from the click TARGET covers the commented anchor itself
    // (always an ancestor-or-self of the target) and, just as importantly, an answerable
    // row nested inside a commented wrapper — a commented poll question holds options.
    if (target.closest?.("[data-review-interactive]")) return null

    // A control is answerable whether or not anyone anchored it. Not every activity
    // marks its parts: a matching board anchors the LEFT column only (the pair reads as
    // one thought), and Fact or Fiction anchors nothing below the block — so a
    // block-level comment would otherwise put a thread over the very cards the reviewer
    // is trying to press. Anchors say "this row is answerable"; this says "this thing is
    // a control", which is true for activities nobody has thought of yet.
    if (target.closest?.(CONTROL_SELECTOR)) return null

    return anchor
  }

  openThread(anchor) {
    this.closeThread()
    this.closeComposer()

    const key = anchor.dataset.reviewAnchorKey
    const entry = this.anchorsValue[key]
    if (!entry) return

    const thread = this.threadTemplateTarget.content.firstElementChild.cloneNode(true)
    const list = thread.querySelector("[data-course-review-thread-list]")

    // The thread carries its own controller (it lives on <body>), so hand it the
    // comments endpoint and context the same way mountComposer hands them to the
    // composer — Resolve / Delete post through these.
    Object.assign(thread.dataset, {
      courseReviewThreadCommentsUrlValue: this.createUrlValue,
      courseReviewThreadContextValue: JSON.stringify({ course_id: this.courseIdValue })
    })

    // The server picks the thread the card acts on (the earliest pending one), so an
    // element whose first thread is already closed still offers Resolve for the rest.
    const primary = entry.comments.find((comment) => comment.id === entry.primary_id) ||
      entry.comments.find((comment) => comment.top_level) || entry.comments[0]
    entry.comments.forEach((comment) => list.appendChild(this.threadItemFor(comment)))
    this.setUpThread(thread, entry, primary)

    this.thread = thread
    document.body.appendChild(thread)

    // The reply composer is the same composer, hosted inside the thread card with
    // parent_id set — identical mechanics, one implementation. It starts collapsed;
    // the footer's Reply button reveals it.
    this.mountComposer({
      anchor,
      trigger: anchor,
      reference: anchor,
      parentId: String(primary?.id || ""),
      host: thread
    })
    this.composer?.classList.add("course-review-composer--collapsed")

    this.threadCleanup = this.centerOn(anchor, thread)
  }

  threadItemFor(comment) {
    const item = this.threadItemTemplateTarget.content.firstElementChild.cloneNode(true)

    item.querySelector(".course-review-thread__avatar").textContent = comment.author_initial
    item.querySelector(".course-review-thread__author").textContent = comment.author_name
    item.querySelector(".course-review-thread__time").textContent = comment.created_label
    // body_html is built server-side by MentionParser from escaped plain text.
    item.querySelector(".course-review-thread__text").innerHTML = comment.body_html

    this.fillThreadAttachments(item, comment.files)

    return item
  }

  fillThreadAttachments(item, files) {
    if (!files || !files.length) return

    const container = item.querySelector("[data-thread-attachments]")
    files.forEach((file) => container.appendChild(this.threadAttachmentFor(file)))
    container.hidden = false
  }

  threadAttachmentFor(file) {
    const node = this.threadAttachmentTemplateTarget.content.firstElementChild.cloneNode(true)
    node.href = file.url

    if (file.image) {
      const thumb = node.querySelector(".course-review-thread__attachment-thumb")
      thumb.src = file.url
      thumb.alt = file.filename
      thumb.hidden = false
    } else {
      node.querySelector(".course-review-thread__attachment-name").textContent = file.filename
      node.querySelector(".course-review-thread__attachment-file").hidden = false
    }

    return node
  }

  // Fills the thread card's header (#N, element name, status pill) and footer
  // (Resolve/Reply/Dismiss, swapping to Reopen once closed) from the anchor entry and
  // its primary top-level comment. Resolve/Dismiss are open to any reviewer; Delete —
  // the kebab's only action — shows only to the comment's own author, so the kebab is
  // dropped entirely when they can't delete.
  setUpThread(thread, entry, comment) {
    if (!comment) return

    thread.dataset.commentId = comment.id
    thread.dataset.commentStatus = comment.status

    thread.querySelector("[data-thread-ordinal]").textContent = entry.ordinal ? `#${entry.ordinal}` : ""
    thread.querySelector("[data-thread-label]").textContent = entry.label || ""
    this.paintThreadStatus(thread, comment.status)
    this.toggleThreadFooter(thread, comment.status)

    if (comment.deletable) {
      thread.querySelector("[data-thread-toolbar]").hidden = false
    } else {
      thread.querySelector("[data-thread-toolbar]")?.remove()
    }
  }

  paintThreadStatus(thread, status) {
    const pill = thread.querySelector("[data-thread-status]")
    pill.textContent = { pending: "Pending", resolved: "Resolved", dismissed: "Dismissed" }[status] || "Pending"
    pill.classList.toggle("tag-pill--amber", status === "pending")
    pill.classList.toggle("course-review-thread__status--resolved", status === "resolved")
    pill.classList.toggle("course-review-thread__status--dismissed", status === "dismissed")
  }

  // Pending → Resolve + Dismiss (and its divider); once closed → Reopen only.
  toggleThreadFooter(thread, status) {
    const pending = status === "pending"
    thread.querySelector("[data-thread-resolve]").hidden = !pending
    thread.querySelector("[data-thread-reopen]").hidden = pending
    thread.querySelector("[data-thread-dismiss]").hidden = !pending
    thread.querySelector("[data-thread-dismiss-divider]").hidden = !pending
  }

  closeThread() {
    if (!this.thread) return

    this.threadCleanup?.()
    this.threadCleanup = null
    this.thread.remove()
    this.thread = null
  }

  // ---- Positioning ----------------------------------------------------------

  floatOn(reference, floating, placement) {
    const updatePosition = () => {
      computePosition(reference, floating, {
        placement,
        strategy: "fixed",
        middleware: [flip(), shift({ padding: 8 })]
      }).then(({ x, y }) => {
        Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
      })
    }

    updatePosition()

    return autoUpdate(reference, floating, updatePosition)
  }

  // Centre the thread card ON the element's centre point, per the design — not off to a
  // side. Placement "bottom" centres it horizontally on the anchor and drops it below;
  // the negative offset (half the anchor + half the card) pulls it back up so the two
  // centres meet. shift() then nudges it inward when the anchor sits near a viewport edge,
  // so a big block or an edge element never pushes the card off-screen.
  centerOn(anchor, floating) {
    const updatePosition = () => {
      computePosition(anchor, floating, {
        placement: "bottom",
        strategy: "fixed",
        middleware: [
          offset(({ rects }) => -(rects.reference.height / 2 + rects.floating.height / 2)),
          shift({ padding: 8 })
        ]
      }).then(({ x, y }) => {
        Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
      })
    }

    updatePosition()

    return autoUpdate(anchor, floating, updatePosition)
  }
}
